/**
 * Public-site furniture (Website.dc.html, Review.dc.html and the Details/Legal/Research pages):
 * the header with the wordmark, the region flags beneath it and a nav that folds under 900px; the
 * footer with the one sentence about what we are not; region flags as data, not decoration.
 *
 * Every string that varies by region is a prop — the pages own their region config; this file owns
 * none of the words.
 */
import type { ReactNode as RN } from "react";
import { h, cx, type ReactNode } from "./h.ts";

/** Standard Unicode regional-indicator flags. Windows falls back to the two letters, which is the right fallback. */
export const FLAG_GLYPH: Record<string, string> = {
  au: "\u{1F1E6}\u{1F1FA}", uk: "\u{1F1EC}\u{1F1E7}", ca: "\u{1F1E8}\u{1F1E6}", us: "\u{1F1FA}\u{1F1F8}", ie: "\u{1F1EE}\u{1F1EA}",
};

export interface RegionFlag { code: string; label: string; href?: string; current?: boolean }
export interface SoonFlag { code: string; label: string }

/** Live regions as links (the current one inert and marked with a 2px ink rule), upcoming ones behind a divider with no href. */
export function RegionFlags({ live, soon }: { live: RegionFlag[]; soon: SoonFlag[] }): ReactNode {
  return h("span", { className: "oc-flags", role: "group", "aria-label": "Region" },
    ...live.map((f) => f.current
      ? h("span", { key: f.code, className: "oc-flag", "aria-current": "true", role: "note", "aria-label": `${f.label} — you are here`, title: `${f.label} — you are here` },
          h("span", { className: "glyph" }, FLAG_GLYPH[f.code] ?? f.code.toUpperCase()), h("span", { className: "lbl" }, `${f.label} — you are here`))
      : h("a", { key: f.code, className: "oc-flag", href: f.href, "aria-label": f.label, title: f.label },
          h("span", { className: "glyph" }, FLAG_GLYPH[f.code] ?? f.code.toUpperCase()), h("span", { className: "lbl" }, f.label))),
    soon.length > 0 ? h("span", { className: "oc-flag-div", "aria-hidden": "true" }) : null,
    ...soon.map((s) => h("span", { key: s.code, className: "oc-flag oc-flag-soon", tabIndex: 0, role: "note", "aria-label": s.label, title: s.label },
      h("span", { className: "glyph" }, FLAG_GLYPH[s.code] ?? s.code.toUpperCase()), h("span", { className: "lbl" }, s.label))),
  );
}

export interface NavLink { label: string; href: string; strong?: boolean }

export function SiteHeader({ homeHref, flags, nav, menuOpen, onToggleMenu, right }: {
  homeHref: string; flags?: RN; nav: NavLink[]; menuOpen: boolean; onToggleMenu: () => void; right?: RN;
}): ReactNode {
  return h("div", { className: "oc-site-head" },
    h("header", null,
      h("span", { style: { display: "flex", flexDirection: "column", alignItems: "flex-start" } },
        h("a", { href: homeHref, className: "oc-wordmark" }, "Operator Capital"),
        flags ?? null),
      right ?? null,
      h("button", { type: "button", className: "oc-menu-btn", onClick: onToggleMenu, "aria-expanded": menuOpen ? "true" : "false", "aria-controls": "oc-nav" }, menuOpen ? "Close" : "Menu"),
      h("nav", { id: "oc-nav", className: cx("oc-nav", menuOpen && "open"), "aria-label": "Sections" },
        ...nav.map((l) => h("a", { key: l.href + l.label, href: l.href, className: cx(l.strong && "strong"), onClick: menuOpen ? onToggleMenu : undefined }, l.label))),
    ));
}

export interface FooterGroup { group: string; links: NavLink[] }

/** `about` is the fixed sentence set (packages/copy); `entityLine` and `adviceLine` come from the region config. */
export function SiteFooter({ about, groups, entityLine, adviceLine }: { about: RN; groups: FooterGroup[]; entityLine: RN; adviceLine: string }): ReactNode {
  return h("footer", { className: "oc-site-foot" },
    h("div", { className: "cols" },
      h("div", { className: "about" }, h("div", { className: "name" }, "Operator Capital"), h("div", { style: { marginTop: "6px" } }, about)),
      h("div", { className: "links" }, ...groups.map((g) => h("div", { key: g.group }, h("span", { className: "group" }, g.group), ...g.links.map((l) => h("a", { key: l.href, href: l.href }, l.label))))),
    ),
    h("div", { className: "oc-mini-legal" }, entityLine, " · ", adviceLine),
  );
}

/** A public-page section: an eyebrow in pencil, a heading, the region name on the right. */
export function Section({ id, eyebrow, regionName, title, lead, children, close = false, level = 2 }: { id?: string; eyebrow?: string; regionName?: string; title?: RN; lead?: RN; children: RN; close?: boolean; level?: 1 | 2 }): ReactNode {
  return h("section", { id, className: cx("oc-band", close && "close") },
    h("div", { className: "oc-wrap oc-section" },
      eyebrow || regionName ? h("div", { style: { display: "flex", flexWrap: "wrap", gap: "8px 24px", alignItems: "baseline", justifyContent: "space-between" } },
        h("div", { className: "eyebrow" }, eyebrow ?? ""), regionName ? h("div", { className: "oc-caption" }, regionName) : null) : null,
      title ? h(level === 1 ? "h1" : "h2", { className: level === 1 ? "oc-h1" : "oc-h2", style: { margin: "6px 0 8px" } }, title) : null,
      lead ? h("p", { className: "oc-body-lg oc-secondary", style: { marginBottom: "30px" } }, lead) : null,
      children,
    ));
}

/** Names in [square brackets and ochre] are placeholders, visible until a human fills them (Design System §18). */
export function Placeholder({ text }: { text: string }): ReactNode {
  return h("span", { className: "oc-placeholder" }, `[${text}]`);
}

/** An entity line that may still carry placeholders: "Operator Capital Pty Ltd · [ABN]". */
export function EntityLine({ text }: { text: string }): ReactNode {
  const parts = text.split(/(\[[^\]]+\])/g).filter((p) => p !== "");
  return h("span", null, ...parts.map((p, i) => p.startsWith("[") ? h(Placeholder, { key: i, text: p.slice(1, -1) }) : h("span", { key: i }, p)));
}

/** The ordered price list (Review.dc.html §5). */
export function PriceRow({ name, when, what, price, last = false }: { name: string; when?: string; what: RN; price: string; last?: boolean }): ReactNode {
  return h("div", { className: cx("oc-price-row", last && "last") },
    h("span", { className: "name" }, name, when ? h("small", null, when) : null),
    h("span", { className: "what" }, what),
    h("span", { className: "price" }, price));
}
